import React, { useEffect } from 'react';

import { useForm } from 'react-hook-form';

import { useAppDispatch } from 'prequalificationV2/hooks/useAppDispatch';
import { useAppSelector } from 'prequalificationV2/hooks/useAppSelector';
import Input from 'components/Inputs/Input';
import PhoneNumberInput from 'components/Inputs/PhoneNumberInput';
import { Button } from 'components/Buttons/Button';
import {
  getMessageForInvalidFields,
  isValidEmail,
  validatePhoneNumber,
} from 'prequalificationV2/utils/validationUtils';
import { getErrorMessageForRequiredFields } from 'prequalificationV2/utils/stringUtils';
import { getIsInitialValueNotNull } from 'prequalificationV2/pages/Dashboard/steps/InformationTabs/AdditionalInformationTab/additionalInformation.utils';
import { LoadingType, setLoadingState } from 'prequalificationV2/actions';
import { DashboardTabName } from 'enums/DashboardTabName';
import { updateApplicationInStore } from 'prequalificationV2/utils/updateApplicationInStore';
import {
  ContractorInformationInputLabel,
  ContractorInformationFormVariableName,
} from 'prequalificationV2/pages/Dashboard/steps/InformationTabs/AdditionalInformationTab/additionalInformation.enums';
import styles from 'prequalificationV2/pages/Dashboard/steps/InformationTabs/InformationTabs.module.scss';
import { hilEmploymentsApi } from 'api/EmploymentsApi';

export interface ContractorInformationForm {
  [ContractorInformationFormVariableName.Name]: string;
  [ContractorInformationFormVariableName.SalespersonName]: string;
  [ContractorInformationFormVariableName.SalespersonPhone]: string;
  [ContractorInformationFormVariableName.SalespersonEmail]: string;
}

const ContractorInformation = () => {
  const dispatch = useAppDispatch();

  const { variables, applicationId, dashboardAdditionalInfo } = useAppSelector((state) => state.application);
  const applicationVariables = variables!;
  const isContractorInformationCompleted = dashboardAdditionalInfo?.isContractorEmploymentCompleted;

  const formMethods = useForm<ContractorInformationForm>({
    defaultValues: {
      [ContractorInformationFormVariableName.Name]: applicationVariables.funded_contractor_name || '',
      [ContractorInformationFormVariableName.SalespersonName]:
        applicationVariables.funded_contractor_salesperson_name || '',
      [ContractorInformationFormVariableName.SalespersonPhone]:
        applicationVariables.funded_contractor_salesperson_phone || '',
      [ContractorInformationFormVariableName.SalespersonEmail]:
        applicationVariables.funded_contractor_salesperson_email || '',
    },
    shouldUnregister: false,
  });

  const {
    register,
    unregister,
    handleSubmit,
    clearErrors,
    formState: { errors },
    watch,
    setValue,
    trigger,
  } = formMethods;
  const watcher = watch();

  useEffect(() => {
    register(ContractorInformationFormVariableName.Name, {
      required: {
        value: !watcher[ContractorInformationFormVariableName.Name],
        message: getErrorMessageForRequiredFields(ContractorInformationInputLabel.Name),
      },
    });
    register(ContractorInformationFormVariableName.SalespersonName, {
      required: {
        value: !watcher[ContractorInformationFormVariableName.SalespersonName],
        message: getErrorMessageForRequiredFields(ContractorInformationInputLabel.SalespersonName),
      },
    });
    register(ContractorInformationFormVariableName.SalespersonPhone, {
      required: {
        value: !watcher[ContractorInformationFormVariableName.SalespersonPhone],
        message: getErrorMessageForRequiredFields(ContractorInformationInputLabel.SalespersonPhone),
      },
      validate: (value) => {
        return validatePhoneNumber(value);
      },
    });
    register(ContractorInformationFormVariableName.SalespersonEmail, {
      required: {
        value: !watcher[ContractorInformationFormVariableName.SalespersonEmail],
        message: getErrorMessageForRequiredFields(ContractorInformationInputLabel.SalespersonEmail),
      },
      validate: (value) => {
        return isValidEmail(value) || getMessageForInvalidFields(ContractorInformationInputLabel.SalespersonEmail);
      },
    });
  }, [register, unregister, watcher]);

  const saveContractorInformation = handleSubmit(async () => {
    dispatch(
      setLoadingState({
        type: `${LoadingType.RequiredInformationUpdating}${DashboardTabName.AdditionalInformation}`,
        loading: true,
      }),
    );

    const updatedApplication = await hilEmploymentsApi.updateAdditionalContractorInformation({
      applicationId: applicationId as string,
      fundedContractorName: watcher[ContractorInformationFormVariableName.Name],
      fundedContractorSalespersonName: watcher[ContractorInformationFormVariableName.SalespersonName],
      fundedContractorSalespersonPhone: watcher[ContractorInformationFormVariableName.SalespersonPhone],
      fundedContractorSalespersonEmail: watcher[ContractorInformationFormVariableName.SalespersonEmail],
    });

    updateApplicationInStore(updatedApplication, dispatch);

    dispatch(
      setLoadingState({
        type: `${LoadingType.RequiredInformationUpdating}${DashboardTabName.AdditionalInformation}`,
        loading: false,
      }),
    );
  });

  return (
    <div>
      <div className={styles.formRow}>
        <Input
          name={ContractorInformationFormVariableName.Name}
          label={ContractorInformationInputLabel.Name}
          value={watcher[ContractorInformationFormVariableName.Name]}
          errorMessage={errors?.[ContractorInformationFormVariableName.Name]?.message}
          inputContainerClassName={styles.input}
          onChange={(event) => {
            clearErrors(ContractorInformationFormVariableName.Name);
            setValue(ContractorInformationFormVariableName.Name, event.target.value);
          }}
          disabled={getIsInitialValueNotNull(applicationVariables.funded_contractor_name)}
        />
      </div>
      <div className={styles.formRow}>
        <Input
          name={ContractorInformationFormVariableName.SalespersonName}
          label={ContractorInformationInputLabel.SalespersonName}
          value={watcher[ContractorInformationFormVariableName.SalespersonName]}
          errorMessage={errors?.[ContractorInformationFormVariableName.SalespersonName]?.message}
          inputContainerClassName={styles.input}
          onChange={(event) => {
            clearErrors(ContractorInformationFormVariableName.SalespersonName);
            setValue(ContractorInformationFormVariableName.SalespersonName, event.target.value);
          }}
          disabled={getIsInitialValueNotNull(applicationVariables.funded_contractor_salesperson_name)}
        />
        <PhoneNumberInput
          name={ContractorInformationFormVariableName.SalespersonPhone}
          label={ContractorInformationInputLabel.SalespersonPhone}
          value={watcher[ContractorInformationFormVariableName.SalespersonPhone]}
          errorMessage={errors?.[ContractorInformationFormVariableName.SalespersonPhone]?.message}
          className={styles.input}
          inputContainerClassname={styles.input}
          onChange={(value: string) => {
            clearErrors(ContractorInformationFormVariableName.SalespersonPhone);
            setValue(ContractorInformationFormVariableName.SalespersonPhone, value);
          }}
          onBlur={() => {
            if (watcher[ContractorInformationFormVariableName.SalespersonPhone]) {
              trigger(ContractorInformationFormVariableName.SalespersonPhone);
            }
          }}
          disabled={getIsInitialValueNotNull(applicationVariables.funded_contractor_salesperson_phone)}
        />
        <Input
          name={ContractorInformationFormVariableName.SalespersonEmail}
          label={ContractorInformationInputLabel.SalespersonEmail}
          value={watcher[ContractorInformationFormVariableName.SalespersonEmail]}
          errorMessage={errors?.[ContractorInformationFormVariableName.SalespersonEmail]?.message}
          inputContainerClassName={styles.input}
          onChange={(event) => {
            clearErrors(ContractorInformationFormVariableName.SalespersonEmail);
            setValue(ContractorInformationFormVariableName.SalespersonEmail, event.target.value);
          }}
          onBlur={() => {
            if (watcher[ContractorInformationFormVariableName.SalespersonEmail]) {
              trigger(ContractorInformationFormVariableName.SalespersonEmail);
            }
          }}
          disabled={getIsInitialValueNotNull(applicationVariables.funded_contractor_salesperson_email)}
        />
      </div>
      {!isContractorInformationCompleted && <Button onClick={saveContractorInformation}>Save</Button>}
    </div>
  );
};

export default ContractorInformation;
